import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import PopUp from 'reusableComponents/PopUp'
import { InputField } from 'reusableComponents/InputField'
import Button from 'react-bootstrap/Button'
import styled, { createGlobalStyle } from 'styled-components'
import { phoneUpdateSendOTP } from 'api/shipping'
import { ReactComponent as UserCheck } from 'assets/svg/user-check.svg'
import SVGIcon from 'reusableComponents/SVGIcon'
import { getDOMEncodeString } from 'utils/helperFunctions'
import { colors } from 'globalStyles'
import VerifyOTPModal from 'components/VerifyOTPModal'

const ModalContentWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  .form-control {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    font-size: 1rem;
  }

  .button-row {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    width: 100%;
    max-width: 24rem;
    margin-left: auto;
    margin-right: auto;
  }
  .button-row .btn {
    flex: 1 1 0%;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    margin: 0 !important;
    box-sizing: border-box;
  }
`

const ChangeMobileModalFooterOverride = createGlobalStyle`
  .change-mobile-modal .modal-footer {
    margin: 0 !important;
    padding: 0 !important;
    max-width: 24rem;
    margin-left: auto !important;
    margin-right: auto !important;
  }
`

const inputCustomStyle = {
  fontSize: '1rem',
  width: '100%',
  boxSizing: 'border-box',
}

const modalBodyStyle = {
  padding: '10px 15px 0 15px',
}

const ChangeMobileNumberModal = ({ show, onCancel, onSuccess }) => {
  const { t, i18n } = useTranslation()
  const [mobileNumber, setMobileNumber] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [showVerificationModal, setShowVerificationModal] = useState(false)

  // Compute formatted number for API and modal
  const formattedNumber = mobileNumber.length === 10 ? `1${mobileNumber}` : ''

  const formatPhoneNumber = (value) => {
    // Format as 123-456-7890
    const cleaned = value.replace(/\D/g, '').slice(0, 10)
    const match = cleaned.match(/(\d{0,3})(\d{0,3})(\d{0,4})/)
    if (!match) return ''
    let formatted = ''
    if (match[1]) formatted += match[1]
    if (match[2]) formatted += (match[2] ? '-' : '') + match[2]
    if (match[3]) formatted += (match[3] ? '-' : '') + match[3]
    return formatted.replace(/-+$/, '')
  }

  const handleInputChange = (e) => {
    // Only allow digits, max 10
    const value = e.target.value.replace(/\D/g, '').slice(0, 10)
    setMobileNumber(value)
    setErrorText('')
  }

  const handleSave = async () => {
    setIsLoading(true)
    setErrorText('')
    try {
      if (mobileNumber.length !== 10) {
        setErrorText(t('my_profile.mobile_number_error_msg'))
        setIsLoading(false)
        return
      }
      await phoneUpdateSendOTP(formattedNumber, i18n.language)
      setShowVerificationModal(true)
    } catch (error) {
      setErrorText(t('my_profile.mobile_number_error_msg'))
    } finally {
      setIsLoading(false)
    }
  }

  // Reset modal state when modal is closed
  useEffect(() => {
    if (!show) {
      setShowVerificationModal(false)
      setMobileNumber('')
      setErrorText('')
    }
  }, [show])

  // Unified handler for closing VerifyOTPModal
  const handleVerificationClose = () => {
    setShowVerificationModal(false)
    // Also close this modal and notify parent if OTP was successful
    if (typeof onSuccess === 'function') onSuccess()
    if (typeof onCancel === 'function') onCancel()
  }

  return (
    <>
      <ChangeMobileModalFooterOverride />
      <PopUp
        className="change-mobile-modal"
        show={show}
        title={t('verificationPopup.mobile_number_label')}
        onHide={onCancel}
        icon={getDOMEncodeString(<SVGIcon Icon={UserCheck} color={colors.primary_black} />)}
        iconWidth="10px"
        size="sm"
        bodyStyle={modalBodyStyle}
        footerStyling
        primaryButton={null}
        secondaryButton={null}
      >
        <ModalContentWrapper>
          <InputField
            id="change-mobile-number-input"
            label={t('verificationPopup.mobile_number_label')}
            value={formatPhoneNumber(mobileNumber)}
            onChange={handleInputChange}
            isRequired
            isInvalid={!!errorText}
            errorText={errorText}
            maxLength={14}
            autoFocus
            autoComplete="off"
            style={inputCustomStyle}
          />
          <div className="button-row">
            <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
              {t('buttonActions.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={isLoading}>
              {isLoading ? t('loading') : t('buttonActions.save')}
            </Button>
          </div>
        </ModalContentWrapper>
        <VerifyOTPModal
          show={showVerificationModal}
          onHide={handleVerificationClose}
          phoneNumber={formattedNumber}
          showModalFor="updatePhone"
          onSuccess={() => {
            // No-op: let handleVerificationClose handle closing
          }}
        />
      </PopUp>
    </>
  )
}

ChangeMobileNumberModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
}

ChangeMobileNumberModal.defaultProps = {
  onSuccess: null,
}

export default ChangeMobileNumberModal
